import React from "react";
import { Trans, useTranslation } from "react-i18next";

const BASE_PAYLOAD = {
  account_name: "",
  contract_id: "",
  access_key: "",
  user_name: "",
  password: "",
  account_no: "",
};

// Field labels are shown in the reader's language; the English here is the fallback.
const FIELD_KEYS = {
  "Shipper number": "shipperNumber",
  "User ID": "userId",
  Password: "password",
  Username: "username",
  "Customer number": "customerNumber",
  "Contract ID": "contractId",
  "Billing account": "billingAccount",
  "Client ID": "clientId",
  "Client secret": "clientSecret",
  "Account number": "accountNumber",
  "Email address": "emailAddress",
  "Account ID": "accountId",
  "Bearer token": "bearerToken",
  "User name": "userName",
  "Duties account number": "dutiesAccountNumber",
};
const field = (name, label, type = "text") => ({
  name,
  label,
  type,
  i18nKey: `carriers.fields.${FIELD_KEYS[label]}`,
});
const secret = (name, label) => field(name, label, "password");
const ACCOUNT_NAME = {
  name: "account_name",
  label: "Account name",
  i18nKey: "carriers.fields.accountName",
  type: "text",
  placeholder: "Display name",
  placeholderKey: "carriers.fields.displayName",
};

const Mail = ({ to }) => (
  <a href={`mailto:${to}`} className="text-brand hover:underline">
    {to}
  </a>
);

const Phone = ({ number }) => (
  <a href={`tel:${number}`} className="text-brand hover:underline">
    {number}
  </a>
);

// What to ask the carrier for, then what the credentials are for.
const Requirement = ({ i18nKey, carrier, carrierKey, components }) => {
  const { t } = useTranslation("settings");
  return (
    <>
      <Trans t={t} i18nKey={i18nKey} components={components} />{" "}
      {t("carriers.connect.outcome", { carrier: carrierKey ? t(carrierKey) : carrier })}
    </>
  );
};

export const CONNECT_FORMS = {
  2: {
    fields: [ACCOUNT_NAME, field("account_no", "Shipper number"), field("user_name", "User ID"), secret("password", "Password")],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.canpar"
        carrier="Canpar"
        components={{ mail: <Mail to="servicedesk@canpar.com" /> }}
      />
    ),
  },
  3: {
    fields: [
      ACCOUNT_NAME,
      field("user_name", "Username"),
      secret("password", "Password"),
      field("account_no", "Customer number"),
      field("contract_id", "Contract ID"),
    ],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.canadaPost"
        carrierKey="carriers.connect.canadaPost"
        components={{ mail: <Mail to="Ivan.Blasco@canadapost.postescanada.ca" /> }}
      />
    ),
  },
  4: {
    fields: [ACCOUNT_NAME, field("user_name", "Username"), secret("password", "Password"), field("account_no", "Billing account")],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.gls"
        carrier="GLS"
        components={{ mail: <Mail to="tech.support@dicomexpress.com" /> }}
      />
    ),
  },
  5: {
    fields: [ACCOUNT_NAME, field("user_name", "Client ID"), secret("password", "Client secret"), field("account_no", "Account number")],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.fedex"
        carrier="FedEx"
        components={{
          site: (
            <a
              href="https://developer.fedex.com/api/en-us/home.html"
              target="_blank"
              rel="noreferrer"
              className="text-brand hover:underline"
            >
              developer.fedex.com
            </a>
          ),
        }}
      />
    ),
  },
  7: {
    // No access_key input: develop's UPS form has it commented out, so it is sent empty.
    fields: [ACCOUNT_NAME, field("account_no", "Account number"), field("user_name", "Client ID"), secret("password", "Client secret")],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.ups"
        carrier="UPS"
        components={{ phone: <Phone number="1-800-782-7892" /> }}
      />
    ),
  },
  8: {
    fields: [ACCOUNT_NAME, field("user_name", "Email address"), secret("password", "Password"), field("account_no", "Billing account")],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.dayRoss"
        carrier="Day & Ross"
        components={{ mail: <Mail to="mydayross@dayandrossinc.ca" /> }}
      />
    ),
  },
  9: {
    fields: [ACCOUNT_NAME, field("user_name", "Account ID"), secret("password", "Password")],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.ics"
        carrier="ICS Courier"
        components={{ mail: <Mail to="kpetrie@icscourier.ca" /> }}
      />
    ),
  },
  10: {
    fields: [ACCOUNT_NAME, field("account_no", "Account number"), secret("access_key", "Bearer token")],
    requirement: (
      <Requirement
        i18nKey="carriers.connect.req.kindersley"
        carrier="Kindersley Transport"
        components={{ mail: <Mail to="anthony.vicenzino@siemenstransport.com" /> }}
      />
    ),
  },
  12: {
    extraPayload: { duties_account_no: "" },
    fields: [
      ACCOUNT_NAME,
      field("user_name", "User name"),
      secret("password", "Password"),
      field("account_no", "Account number"),
      field("duties_account_no", "Duties account number"),
    ],
    requirement: (
      <Requirement i18nKey="carriers.connect.req.dhl" carrier="DHL Transport" />
    ),
  },
  13: {
    extraPayload: { duties_account_no: "" },
    fields: [
      ACCOUNT_NAME,
      field("user_name", "User name"),
      secret("password", "Password"),
      field("account_no", "Account number"),
      secret("access_key", "Bearer token"),
    ],
    requirement: (
      <Requirement i18nKey="carriers.connect.req.xpo" carrier="XPO Transport" />
    ),
  },
};

export const emptyPayload = (form) => ({ ...BASE_PAYLOAD, ...form.extraPayload });

const storedCarriers = () => {
  try {
    return JSON.parse(localStorage.getItem("Carriers") || "[]") ?? [];
  } catch {
    return [];
  }
};

export const connectableCarriers = () =>
  storedCarriers().filter((carrier) => CONNECT_FORMS[carrier?.id]);
